import {FC, useCallback, useState} from 'react'
import {
  ACCRUAL_PERIOD,
  CASH_REGISTER_CODE,
  CURRENCY,
  EXPENSE_NAME,
  IBAN,
  ICarMTVModel,
  initialCarMTV,
  PAYMENT_TYPE,
  tabOptions,
  validationCarMTV,
} from './CarMTVModel'
import {createCarMTV, deleteCarMTV, loadCarMTV, updateCarMTV} from './Api'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {Form, Formik, FormikProps} from 'formik'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import {ToastContainer} from 'react-toastify'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput' 
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import { AsyncCarModel } from '../../../../shared/models/ApiModels'
import { fetchAsyncCarData } from '../../../../shared/models/SharedApiLookups'

const CarMTVPage: FC = () => {
  const [tab, setTab] = useState<string | 'description'>('description')
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [carMTV, setCarMtv] = useState<ICarMTVModel[]>([])

  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])

  const fetchCarMTV = useCallback(
    async (page: number = 0, _sort?: string, _order?: 'asc' | 'desc') => {
      try {
        setLoading(true)
        const res = await loadCarMTV(page)
        const data = res.data.payload || res.data
        setCarMtv(data.content || data || [])
        setTotalPages(data.totalPages || 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
        }
        console.error(err)
        showErrorToast('Kayıtlar getirilirken hata oluştu.')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const handleSave = async (values: ICarMTVModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateCarMTV({...values, id: selectedId})
        showSuccessToast('Araç Tipi başarıyla güncellendi')
      } else {
        await createCarMTV(values)
        showSuccessToast('Araç Tipi başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchCarMTV(0, sortBy, sortOrder)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (loc: ICarMTVModel, formik: FormikProps<ICarMTVModel>) => {
    formik.setValues(loc)
    setSelectedId(loc.id)
    setTab('form')
  }

  const handleDeleteClick = useCallback((row: ICarMTVModel) => {
    setSelectedId(row.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await deleteCarMTV(selectedId)
      showSuccessToast('Araç Tipi başarıyla silindi')
      await fetchCarMTV(currentPage, sortBy, sortOrder)
    } catch (err) {
      console.error(err)
      showErrorToast('Araç Tipi silinirken bir hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchCarMTV(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchCarMTV]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchCarMTV(currentPage, column, order)
    },
    [currentPage, fetchCarMTV]
  )

  return (
    <>
      <Formik
        initialValues={initialCarMTV}
        validationSchema={validationCarMTV}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='carMTVForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              <div className='card mb-5'>
                <Form id='carMTVForm' className='form card-body'>
                  {tab === 'description' && (
                    <>
                      <div className='row mb-6'>
                        <TextInput
                          name='accrualDate'
                          type='date'
                          label='Tahakkuk Tarihi'
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='expenseName'
                          label='Gider Adı'
                          wrapperClassName='col-md-6'
                          options={EXPENSE_NAME}
                        />
                      </div>
                      <div className='row mb-6'>
                        <SelectInput
                          name='accrualPeriod'
                          label='Tahakkuk Dönemi'
                          wrapperClassName='col-md-6'
                          options={ACCRUAL_PERIOD}
                        />
                        <SelectInput
                          name='transactionBranch'
                          label='İşlem Şube'
                          wrapperClassName='col-md-6'
                          options={branchOptions}
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='amount'
                          label='Tutar'
                          wrapperClassName='col-md-2'
                          type='number'
                        />
                        <SelectInput
                          name='currency'
                          label='Birim'
                          wrapperClassName='col-md-2'
                          options={CURRENCY}
                        />
                        <TextInput
                          name='exchangeRate'
                          label='Kur'
                          type='number'
                          wrapperClassName='col-md-2'
                        />
                        <TextInput name='documentNo' label='Evrak No' wrapperClassName='col-md-6' />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='remaining'
                          label='Kalan'
                          wrapperClassName='col-md-6'
                          type='number'
                        />
                        <TextInput
                          name='processedBy'
                          label='İşlem Yapan'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <AsyncSearchInput<AsyncCarModel>
                          name="carId"
                          label='Araç Ara'
                          placeholder='Araç bilgisi girin...'
                          fetchHandler={fetchAsyncCarData}
                          displayField={(item) => `${item.plate} (${item.modelYear})`}
                          onSelect={(item) => console.log('Seçilen araç:', item)}
                          onClear={() => console.log('Araç temizlendi')}
                        />
                        <TextInput name='findCar' label='Araç Ara' wrapperClassName='col-md-6' />
                        <TextInput
                          name='findOrganisation'
                          label='Kurum Ara'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                    </>
                  )}
                  {tab === 'payment' && (
                    <>
                      <div className='row mb-6'>
                        <TextInput
                          name='paymentDate'
                          type='date'
                          label='Ödeme Tarihi'
                          wrapperClassName='col-md-6'
                        />
                        <TextAreaInput
                          name='description'
                          label='Açıklama'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-6'>
                        <TextInput
                          name='paymentAmount'
                          label='Ödeme'
                          wrapperClassName='col-md-2'
                          type='number'
                        />
                        <SelectInput
                          name='currency'
                          label='Birim'
                          wrapperClassName='col-md-2'
                          options={CURRENCY}
                        />
                        <TextInput
                          name='exchangeRate'
                          label='Kur'
                          type='number'
                          wrapperClassName='col-md-2'
                        />
                      </div>
                      <div className='mb-6'>
                        <SelectInput
                          name='paymentType'
                          label='Ödeme Türü'
                          wrapperClassName='col-md-6'
                          options={PAYMENT_TYPE}
                        />
                      </div>
                      <div className='mb-6'>
                        <SelectInput
                          name='cashRegisterCode'
                          label='Kasa Kodu'
                          wrapperClassName='col-md-6'
                          options={CASH_REGISTER_CODE}
                        />
                      </div>
                      <div className='mb-6'>
                        <SelectInput
                          name='iban'
                          label='IBAN ( Hesap No)'
                          wrapperClassName='col-md-6'
                          options={IBAN}
                        />
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default CarMTVPage
